"use client";

import React from "react";
import { PhoneCall, TriangleAlert as AlertTriangle, Clock, ShieldCheck, Wrench, Droplet, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const commonIssues = [{
  title: "Severe Clogs & Overflows",
  desc: "Fast emergency response before water damages your flooring and ceiling.",
  icon: AlertTriangle
}, {
  title: "Continuous Running Water",
  desc: "Faulty flappers, fill valves, and leaks that spike your monthly water bill.",
  icon: Droplet
}, {
  title: "Base Leaks & Wobbling",
  desc: "Compromised wax rings or cracked porcelain risking structural rot.",
  icon: Wrench
}];
export default function ToiletHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="toiletissueshero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 border-b border-border">
      {/* Subtle background decorative shapes */}
      <div className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-40">
        <div className="h-[480px] w-[480px] rounded-full bg-secondary/60 blur-3xl" />
        <div className="absolute top-10 right-10 h-72 w-72 rounded-full bg-accent/20 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Main Copy Area */}
          <div className="lg:col-span-7 flex flex-col items-start gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-card text-foreground px-3 py-1 font-semibold border-border shadow-xs">
                <Clock className="size-3.5 text-accent" />
                24/7 Rapid Response in Jonesboro
              </Badge>
              <Badge variant="secondary" className="bg-secondary text-secondary-foreground px-3 py-1 font-medium">
                <ShieldCheck className="size-3.5 text-primary" />
                Licensed & Insured Plumbers
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-default font-extrabold tracking-tight text-foreground leading-[1.15]">
                Toilet Backing Up, Leaking, or Refusing to Flush?
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                A malfunctioning toilet isn’t just an inconvenience—it’s an urgent health and water damage risk. 
                Jonesboro Emergency Plumber delivers prompt diagnostics, complete clearing, and reliable repairs day or night.
              </p>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-2">
              <Button size="lg" asChild className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-6 rounded-md shadow-md text-base font-semibold">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                  <PhoneCall className="size-5 text-accent" />
                  <span>Call Emergency Dispatch</span>
                </a>
              </Button>

              <Button size="lg" variant="outline" asChild className="border-border bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground h-12 px-6 rounded-md shadow-xs text-base font-medium">
                <Link to="/toilet-repair/" className="flex items-center justify-center gap-2">
                  <span>Explore Toilet Services</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Trust checklist */}
            <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-muted-foreground w-full">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Zero hidden dispatch surcharges</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Equipped for high-pressure unclogging</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Replacement parts in fully stocked vans</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>Local Jonesboro plumbers ready nearby</span>
              </div>
            </div>
          </div>

          {/* Right Visual & Quick Common Problem Cards */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card p-2 shadow-lg">
              <div className="relative h-64 sm:h-72 w-full overflow-hidden rounded-xl bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_repair_emergency_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_repair_emergency_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_repair_emergency_hero.png-wvc-sizes" alt="Jonesboro professional plumber inspecting a residential toilet system" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent flex items-end p-5">
                  <div className="text-background">
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent">Emergency Diagnostic</p>
                    <p className="text-sm font-medium">On-site troubleshooting for persistent flapper, valve, and sewer blockages.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Common issue breakout cards */}
            <div className="flex flex-col gap-3">
              {commonIssues.map((issue, i) => {
              const IconComponent = issue.icon;
              return <motion.div key={issue.title} data-index={i} whileHover={shouldReduceMotion ? {} : {
                x: 4
              }} className="flex items-start gap-3.5 p-3.5 rounded-xl border border-border bg-card/80 text-card-foreground shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                      <IconComponent className="size-4.5 text-primary" />
                    </div>
                    <div className="space-y-0.5">
                      <h2 className="text-sm font-semibold font-default text-card-foreground">
                        {issue.title}
                      </h2>
                      <p className="text-xs text-muted-foreground leading-snug">
                        {issue.desc}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>

        </div>
      </div>
    </section>;
}